import { useQuery } from "@tanstack/react-query";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import {
  type PerformanceWithAccountFilter,
  type QueryFilterFlow,
  completes,
  fallbackToZero,
  formattedAverageTime,
  lineChartFactory,
  starts,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";

import * as contentReportingService from "features/Reporting/services/contentReportingService";
import { mockFlowEmptyEngagement } from "features/Reporting/mocks/contentData";
import { CHART_PERIOD } from "hooks/useChartPeriodMeasure";

export const generateUserFlowStatistics = (
  started: number,
  completed: number,
  retaken: number,
  averageCompletionTime: number | null,
): CardReportingItem[] => [
  {
    statDescription: starts,
    stat: started,
    popupHeader: starts,
    popupBody: "Number of users who have started this flow.",
    width: "15.9rem",
  },
  {
    statDescription: completes,
    stat: completed,
    popupHeader: completes,
    popupBody: "Number of users who have completed this flow.",
    width: "12.3rem",
  },
  {
    statDescription: "Retaken",
    stat: retaken,
    popupHeader: "Retaken",
    popupBody: "Total number of times this flow has been retaken by users.",
    width: "15.9rem",
  },
  {
    statDescription: "Avg. Completion Time",
    stat: formattedAverageTime(fallbackToZero(averageCompletionTime)),
    popupHeader: "Avg. Completion Time",
    popupBody: "Average time required to complete the Flow (calculated only for users who completed the Flow).",
    width: "15.9rem",
  },
];

const fetchDailyData = async ({ queryKey }: QueryFilterFlow) => {
  const [, filter] = queryKey;
  const result = await contentReportingService.getFlowsDailyData(filter);
  return lineChartFactory(result.data);
};

export const fetchEngagementQuery = async ({ queryKey }: QueryFilterFlow) => {
  const [, filter] = queryKey;

  const result = await contentReportingService.getFlowsEngagement(filter);
  const data = {
    ...mockFlowEmptyEngagement,
    ...result.data,
  };

  const barData: DataPoint[] = [
    {
      id: starts.toLowerCase(),
      category: starts,
      value: data.Started,
      fillColor: reportingColors.blue,
    },
    {
      id: completes.toLowerCase(),
      category: completes,
      value: data.Completed,
      fillColor: reportingColors.yellow,
    },
  ];

  const cardDataIndividual = generateUserFlowStatistics(
    data.Started,
    data.Completed,
    data.Retaken,
    data.AverageCompletionTime,
  );

  const cardData: CardReportingItem[] = [
    {
      stat: data.Customers,
      popupBody: "The total number of customers across all flows.",
      statDescription: "Customers",
    },
    {
      stat: data.AverageCustomerSize,
      popupBody: "The average account size across all flows in your account.",
      statDescription: "Avg. Customer Size",
    },
    {
      stat: data.Flows,
      popupBody: "The total number of flows in your account.",
      statDescription: "Flows",
    },
    {
      stat: data.Users,
      popupBody: "The total number of users who have access to any of your flows in this account.",
      statDescription: "Users",
    },
    {
      stat: data.AverageFlowsUser,
      popupBody: "The average number of users per flow across all flows in your account.",
      statDescription: "Avg. Flows/User",
    },
  ];

  return { ...data, barData, cardData, cardDataIndividual };
};

export const useFlowLineChartQuery = (
  filter: PerformanceWithAccountFilter | null,
  aggregation: string,
  flowId?: string | number,
  versionNumber?: number | null,
) => {
  return useQuery({
    queryKey: ["FlowReportDailyData", { ...filter, aggregation, flowId }],
    queryFn: fetchDailyData,
    enabled: !!filter && aggregation !== CHART_PERIOD.UNSET && versionNumber !== null && filter.type !== undefined,
  });
};

export const useFlowEngagementQuery = (
  filter: PerformanceWithAccountFilter | null,
  flowId?: string | number,
  versionNumber?: number | null,
) => {
  return useQuery({
    queryKey: ["FlowReportEngagement", { ...filter, flowId }],
    queryFn: fetchEngagementQuery,
    enabled: !!filter && versionNumber !== null && filter.type !== undefined,
  });
};
